import { create } from 'zustand'

import { automationManager } from '@/modules/automation/services'
import type {
  AutomationConfig,
  AutomationDashboard,
  AutomationEvent,
  AutomationFilters,
  AutomationJob,
  AutomationLog,
  AutomationPriority,
  AutomationSession,
  AutomationTask,
} from '@/modules/automation/types'

interface AutomationStoreState {
  ready: boolean
  dashboard: AutomationDashboard
  jobs: AutomationJob[]
  sessions: AutomationSession[]
  tasks: AutomationTask[]
  logs: AutomationLog[]
  events: AutomationEvent[]
  notifications: Array<{ id: string; type: string; message: string; at: string }>
  config: AutomationConfig
  filters: AutomationFilters

  hydrate: () => void
  setFilters: (filters: Partial<AutomationFilters>) => void
  updateConfig: (patch: Partial<AutomationConfig>) => void
  enqueue: (input: {
    supplier: string
    operation: string
    priority?: AutomationPriority
    payload?: Record<string, unknown>
  }) => AutomationTask
  refresh: () => void
}

export const useAutomationStore = create<AutomationStoreState>((set, get) => ({
  ready: false,
  dashboard: { queued: 0, running: 0, completed: 0, failed: 0, averageDuration: 0 },
  jobs: [],
  sessions: [],
  tasks: [],
  logs: [],
  events: [],
  notifications: [],
  config: automationManager.getConfig(),
  filters: { status: 'ALL' },

  hydrate: () => {
    const filters = get().filters
    set({
      ready: true,
      dashboard: automationManager.dashboard(),
      jobs: automationManager.listJobs(filters),
      sessions: automationManager.listSessions(),
      tasks: automationManager.listTasks(),
      logs: automationManager.listLogs(),
      events: automationManager.listEvents(),
      notifications: automationManager.listNotifications(),
      config: automationManager.getConfig(),
    })
  },

  setFilters: (filters) => {
    const next = { ...get().filters, ...filters }
    set({
      filters: next,
      jobs: automationManager.listJobs(next),
    })
  },

  updateConfig: (patch) => {
    const config = automationManager.updateConfig(patch)
    set({ config })
  },

  enqueue: (input) => {
    const task = automationManager.enqueue(input)
    get().refresh()
    return task
  },

  refresh: () => {
    const filters = get().filters
    set({
      dashboard: automationManager.dashboard(),
      jobs: automationManager.listJobs(filters),
      sessions: automationManager.listSessions(),
      tasks: automationManager.listTasks(),
      logs: automationManager.listLogs(),
      events: automationManager.listEvents(),
      notifications: automationManager.listNotifications(),
    })
  },
}))
